import { useMutation } from '@tanstack/react-query'
import {
  App,
  Button,
  Checkbox,
  Collapse,
  Flex,
  Form,
  Input,
  Tooltip,
  Typography
} from 'antd'
import { createStyles } from 'antd-style'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import {
  mfaSendOtpCreate,
  mfaVerifyOtpCreate,
  usersLoginAdminCreateMutation
} from 'src/api/main'
import { LOGIN_IN_PROGRESS_KEY } from 'src/constants'
import { useResendCountdown } from 'src/features/auth/hooks'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import {
  setDeviceToken,
  setLocalStorageToken,
  useInlineApiErrors
} from 'src/utils'

import { AuthHeader } from '../components/AuthHeader'
import { schedulePasskeyPrompt } from '../components/PasskeyRecommendationModal'
import { asAuthenticatedTokens } from '../utils/authResponseAdapters'

const { OTP } = Input
const { Text } = Typography

const useStyles = createStyles(() => ({
  otp: {
    'input.ant-otp-input': {
      width: '36px !important',
      height: '36px !important'
    }
  }
}))

type MfaLocationState = {
  __mfaAccessToken?: string
  __mfaSetupRequired?: boolean
  __created_at?: string
  expires_in?: number
  wait?: number
  channel?: string
  masked_destination?: string
}

const getDeviceName = () => {
  const ua = navigator.userAgent
  const platform = navigator.platform

  return `${platform} - ${ua}`
}

export const ConfirmMfaOtp = () => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { message } = App.useApp()
  const { showApiErrors, errorAlert } = useInlineApiErrors()
  const { updateUserData } = useSessionContext()

  const locationState = useMemo(
    () => location.state as MfaLocationState | null,
    [location.state]
  )

  // Compelled-but-unenrolled members are routed here to set up their second
  // factor (the gate provisioned the email-OTP fallback); frame the same OTP
  // step as enrollment rather than a routine challenge.
  const isSetup = Boolean(locationState?.__mfaSetupRequired)

  const mfaHeaders = useMemo(
    () =>
      locationState?.__mfaAccessToken
        ? { Authorization: `Token ${locationState.__mfaAccessToken}` }
        : undefined,
    [locationState]
  )
  const expirationTime = useResendCountdown({
    createdAt: locationState?.__created_at,
    expiresInSeconds: locationState?.expires_in,
    waitSeconds: locationState?.wait
  })

  useEffect(() => {
    if (locationState?.__mfaAccessToken) return
    navigate(paths.auth.login, { replace: true })
  }, [locationState, navigate])

  useEffect(() => {
    if (expirationTime.expired) {
      form.setFields([
        {
          name: 'otp_code',
          errors: [t('auth.mfa.codeExpired')],
          value: ''
        }
      ])
    } else {
      form.resetFields(['otp_code'])
    }
  }, [expirationTime.expired, form, t])

  const { mutateAsync: fetchUserAfterLogin, isPending: isPendingUser } =
    useMutation({
      ...usersLoginAdminCreateMutation(),
      onSuccess: (response) => {
        sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
        updateUserData(response)
        message.success(t('login_success'))
        navigate(paths.home)
      },
      onError: (e) => {
        sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
        showApiErrors(e)
      }
    })

  const { mutate: verifyOtp, isPending: isVerifyPending } = useMutation({
    mutationFn: async (values: {
      otp_code: string
      trusted_device?: boolean
    }) => {
      const { data } = await mfaVerifyOtpCreate({
        headers: mfaHeaders,
        body: {
          otp_code: values.otp_code,
          trusted_device: values.trusted_device,
          device_name: getDeviceName()
        },
        throwOnError: true
      })

      return data
    },
    onSuccess: async (response) => {
      const tokens = asAuthenticatedTokens(response)

      if (tokens) {
        // Persist trusted-device token so the next login can skip MFA
        const deviceToken = (response as Record<string, unknown>)
          ?.device_token as string | undefined
        if (deviceToken) {
          setDeviceToken(deviceToken)
        }

        schedulePasskeyPrompt()
        setLocalStorageToken(tokens)
        await fetchUserAfterLogin({})
        return
      }

      showApiErrors(new Error('Missing tokens in MFA response'))
    },
    onError: (error) => {
      const axiosError = error as {
        response?: { data?: { code?: string; detail?: string } }
      }
      const code = axiosError.response?.data?.code

      if (code === 'token_not_valid') {
        message.error(t('auth.mfa.tokenExpired'))
        navigate(paths.auth.login, { replace: true })
        return
      }

      if (code?.includes('otp')) {
        const detail = axiosError.response?.data?.detail

        if (detail) {
          form.setFields([{ name: 'otp_code', errors: [detail] }])
          return
        }
      }

      showApiErrors(error)
    }
  })

  const { mutate: resendOtp, isPending: isResendPending } = useMutation({
    mutationFn: async () => {
      const { data } = await mfaSendOtpCreate({
        headers: mfaHeaders,
        throwOnError: true
      })

      return data
    },
    onSuccess: (data) => {
      message.success(t('auth.mfa.otpSent'))
      navigate(
        { pathname: location.pathname },
        {
          replace: true,
          state: {
            ...locationState,
            ...(data as Record<string, unknown>),
            __created_at: new Date().toISOString()
          }
        }
      )
    },
    onError: (error) => {
      const axiosError = error as {
        response?: { data?: { code?: string } }
      }

      if (axiosError.response?.data?.code === 'token_not_valid') {
        message.error(t('auth.mfa.tokenExpired'))
        navigate(paths.auth.login, { replace: true })
        return
      }

      showApiErrors(error)
    }
  })

  const onSubmit = async () => {
    try {
      const values = await form.validateFields()

      verifyOtp(values)
    } catch {
      // Validation errors shown by form
    }
  }

  const resendTooltipTitle = expirationTime.canResend
    ? undefined
    : t('auth.resendInSeconds', {
        count: expirationTime.resendInSeconds
      })

  return (
    <Flex vertical gap={16} style={{ width: '100%' }}>
      <AuthHeader
        title={t(isSetup ? 'auth.mfa.setupTitle' : 'auth.mfa.otpTitle')}
        subtitle={t(
          isSetup ? 'auth.mfa.setupDescription' : 'auth.mfa.otpDescription'
        )}
      />

      {errorAlert}

      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="otp_code"
          label={t('auth.mfa.otpLabel')}
          rules={[{ required: true, message: t('auth.mfa.otpRequired') }]}
        >
          <OTP
            autoFocus
            disabled={expirationTime.expired}
            size="large"
            formatter={(str) => str.replace(/\D/g, '')}
            className={styles.otp}
            onChange={(value) => {
              if (
                value.length === 6 &&
                !expirationTime.expired &&
                !isVerifyPending
              ) {
                verifyOtp({
                  otp_code: value,
                  trusted_device: form.getFieldValue('trusted_device')
                })
              }
            }}
          />
        </Form.Item>

        <Flex align="center" gap={4} style={{ marginBottom: 16 }}>
          {!expirationTime.expired && (
            <>
              <Text type="secondary">
                {t('auth.mfa.codeExpiresIn', {
                  time: expirationTime.formattedTime
                })}
              </Text>
              <Text type="secondary" aria-hidden>
                &middot;
              </Text>
            </>
          )}
          <Tooltip title={resendTooltipTitle}>
            <span style={{ display: 'inline-flex' }}>
              <Button
                type="link"
                disabled={!expirationTime.canResend}
                loading={isResendPending}
                onClick={() => resendOtp()}
                style={{ paddingInline: 0 }}
              >
                {t('auth.mfa.resendOtp')}
              </Button>
            </span>
          </Tooltip>
        </Flex>

        <Form.Item
          name="trusted_device"
          valuePropName="checked"
          style={{ marginBottom: 16 }}
        >
          <Checkbox>{t('auth.mfa.keepMeLoggedIn')}</Checkbox>
        </Form.Item>

        <Flex gap={12} vertical>
          {/* A member enrolling for the first time has no backup codes yet. */}
          {!isSetup && (
            <Collapse
              size="small"
              items={[
                {
                  key: 'backup',
                  showArrow: false,
                  label: (
                    <Flex justify="center">
                      <Text strong>{t('auth.mfa.moreOptions')}</Text>
                    </Flex>
                  ),
                  children: (
                    <Link
                      to={paths.auth.confirmMfaBackupCode}
                      state={locationState}
                    >
                      <Button block type="primary">
                        {t('auth.mfa.useBackupCode')}
                      </Button>
                    </Link>
                  )
                }
              ]}
            />
          )}

          <Button
            block
            size="large"
            type="primary"
            loading={isVerifyPending || isPendingUser}
            onClick={onSubmit}
          >
            {t('sign_in')}
          </Button>
        </Flex>
      </Form>
    </Flex>
  )
}
